import {
  getAccessToken,
  convertExpirationSecondsToDate,
  getAuthorizationHeaders,
} from './auth';
import * as ENDPOINTS from './endpoints';
import LoginResponse from '@/types/LoginResponse';
import Invoice from '@/types/Invoice';
import Billing from '@/types/Billing';

const sendRequest = async (
  endpoint: string,
  isAuthReq: boolean,
  errorKey: string,
  options: object,
): Promise<object> => {
  const accessToken: string | null = isAuthReq ? await getAccessToken() : '';
  const mergedOptions: RequestInit = {
    cache: 'no-cache',
    ...options,
  };

  if (isAuthReq) {
      mergedOptions.headers = {
          ...mergedOptions.headers,
          Authorization: `Bearer ${accessToken}`,
      };
  }

  const response: any = await fetch(endpoint, mergedOptions).then((r: any): any => r);
  let jsonResponse: any;
  // catch error if no response is available
  try {
    jsonResponse = await response.json();
  } catch {
    jsonResponse = {};
  }

  if (response.status >= 400 && response.status < 600) {
    throw new Error(jsonResponse[errorKey] || 'unable to process your request');
  }

  return jsonResponse;
};

const postRequest = async (
  endpoint: string,
  isAuthReq: boolean,
  body: FormData | string,
  errorKey: string,
  options: object = {},
): Promise<object> => {
  const mergedOptions: object = {
    body,
    ...options,
    method: 'POST',
  };

  return sendRequest(endpoint, isAuthReq, errorKey, mergedOptions);
};

const putRequest = async (
  endpoint: string,
  isAuthReq: boolean,
  body: FormData | string,
  errorKey: string,
  options: object = {},
): Promise<object> => {
  const mergedOptions: object = {
    body,
    method: 'PUT',
    ...options,
  };

  return sendRequest(endpoint, isAuthReq, errorKey, mergedOptions);
};

const getRequest = async (
  endpoint: string,
  isAuthReq: boolean,
  errorKey: string,
  options: object = {},
): Promise<object> => {
  const mergedOptions: object = {
    method: 'GET',
    ...options,
  };

  return sendRequest(endpoint, isAuthReq, errorKey, mergedOptions);
};

export const sendLoginRequest = async (loginFormData: FormData): Promise<LoginResponse> => {
  const options: object = {
    'headers': getAuthorizationHeaders(),
    'content-type': 'multipart/form-data',
  };
  const responseObject: any = await postRequest(
    ENDPOINTS.authEndpoint,
    false,
    loginFormData,
    'error_description',
    options,
  );
  responseObject.expires_in = convertExpirationSecondsToDate(responseObject.expires_in);

  return responseObject as LoginResponse;
};

export const getNewAccessToken = async (refreshFormData: FormData): Promise<LoginResponse> => {
  const options: object = {
    'headers': getAuthorizationHeaders(),
    'content-type': 'multipart/form-data',
  };
  const responseObject: any = await postRequest(
    ENDPOINTS.authEndpoint,
    false,
    refreshFormData,
    'error_description',
    options,
  );
  responseObject.expires_in = convertExpirationSecondsToDate(responseObject.expires_in);

  return responseObject as LoginResponse;
};

export const sendResetLeadRequest = async (saleId: string): Promise<object> => {
  if (!saleId) {
    throw new Error('Sale Id has to be set!');
  }
  const endpoint: string = ENDPOINTS.resetLead(saleId);
  const response: object = await putRequest(endpoint, true, '', 'message');

  return response;
};

export const uploadSkippedNetwork = async (file: File | null): Promise<object> => {
    if (!file) {
        throw new Error('You have to attach a skipped network first');
    }

    const body: FormData = new FormData();

    body.append('file', file);

    return await postRequest(ENDPOINTS.skipInvoicingImport, true, body, 'message');
};

export const uploadSettingsNetwork = async (file: File | null): Promise<object> => {
    if (!file) {
        throw new Error('You have to attach a settings network first');
    }

    const body: FormData = new FormData();

    body.append('file', file);

    return await postRequest(ENDPOINTS.settingsNetworkImport, true, body, 'message');
};

export const duplicateInvoice = async (invoiceNumber: string): Promise<string> => {
  if (!invoiceNumber) {
    throw new Error('Invoice number has to be set!');
  }
  const endpoint: string = ENDPOINTS.duplicateInvoice(invoiceNumber);
  const response: any = await postRequest(endpoint, true, '', 'message', {
      headers: {
          'content-Type': 'application/json',
      },
  });

  return ENDPOINTS.easybillLink(response.easybillDocumentId);
};

export const sendInvoicesRequest = async (
  invoiceIds: Set<string>,
): Promise<object> => {
  if (invoiceIds.size === 0) {
    throw new Error('You have to select some invoices first');
  }

  const body: object = {
    invoiceIds: Array.from(invoiceIds.values()),
  };
  const endpoint: string = ENDPOINTS.sendInvoices;
  const response: any = await postRequest(endpoint, true, JSON.stringify(body), 'message', {
      headers: {
          'content-Type': 'application/json',
      },
  });

  return response;
};

export const getDraftInvoicesRequest = async (
  month: number,
  year: number,
): Promise<Invoice[]> => {
  if (!year || year < 2017) {
    throw new Error('Year has to be set!');
  }
  if (!month || month <= 0 || month > 12) {
    throw new Error('Month has to be set!');
  }

  const body: object = {
    size: 1000,
    filter: {
      month,
      year,
    },
  };
  const endpoint: string = ENDPOINTS.getInvoices;
  const response: any = await postRequest(endpoint, false, JSON.stringify(body), 'message', {
      headers: {
          'content-Type': 'application/json',
      },
  });
  if (!response.content) {
    throw new Error('No Draft Invoices for that month!');
  }

  const invoices: Invoice[] = response.content.map((document: any) => {
    const { id, name, email } = document;

    return {
      id,
      customerName: `${name} (${email})`,
      invoiceLink: ENDPOINTS.easybillLink(id),
    };
  });

  return invoices;
};

export const getLastBillingRequest = async (): Promise<Billing> => {
  const options: object = {
    'content-Type': 'application/json',
  };
  const endpoint: string = ENDPOINTS.getLastBilling;
  const response: any = await getRequest(endpoint, true, '', options);

  return response;
};

export const createNewBillingRequest = async (): Promise<Billing> => {
  const options: object = {
    'content-Type': 'application/json',
  };
  const endpoint: string = ENDPOINTS.newBilling;
  const response: any = await postRequest(endpoint, true, '', '', options);

  return response;
};
